import { Device, FRESH_INSTALL_VERSION, INSTALLABLE_CORE_PACKAGES, Versions } from "@app/shared";
import { useEffect, useState } from "react";
import { compareVersions } from "../../helpers";
import { Spinner } from "./loading";
import { SortableTable, SortableTableHeaderType } from "./SortableTable";

/**
 * One selectable package row aggregated across every selected device. `id` doubles as the
 * package name — SortableTable requires an id, and apt package names are unique.
 */
type BatchUpgradeRow = {
    id: string;
    name: string;
    /** Distinct versions the selected devices would move to, oldest first ("latest" = fresh install). */
    availableVersions: string;
    /** How many of the selected devices this package would actually change. */
    deviceCount: number;
};

type BatchDeviceUpgradeViewProps = {
    devices: Device[];
    /** Fired on every checkbox change with the currently selected package names. */
    onSelectionChange: (packageNames: string[]) => void;
};

/**
 * Package picker for a batch upgrade: the union of core (`os-*`) upgrades pending on any of the
 * selected devices, plus known core packages some devices are missing (see
 * INSTALLABLE_CORE_PACKAGES). The chosen packages are sent to EVERY selected device — apt treats
 * an already-current package as a no-op, so the union is safe to broadcast — which keeps the
 * semantics predictable ("these packages will be current on all selected devices") rather than
 * per-device cherry-picking.
 */
export const BatchDeviceUpgradeView: React.FC<BatchDeviceUpgradeViewProps> = ({ devices, onSelectionChange }) => {
    const [rows, setRows] = useState<BatchUpgradeRow[]>();
    const [noReportCount, setNoReportCount] = useState(0);
    const [selected, setSelected] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (!devices.length) return;
        let cancelled = false;
        (async () => {
            // One report per device, individually error-tolerant: a device that has never
            // reported (or a failed request) must not sink the whole dialog.
            const reports = await Promise.all(devices.map(async (device) => {
                try {
                    return await Versions.latestByDeviceId(device.id);
                } catch {
                    return undefined;
                }
            }));
            if (cancelled) return;

            const byName = new Map<string, { versions: Set<string>; deviceCount: number }>();
            const add = (name: string, version: string) => {
                const entry = byName.get(name) ?? { versions: new Set<string>(), deviceCount: 0 };
                entry.versions.add(version);
                entry.deviceCount++;
                byName.set(name, entry);
            };

            let missingReports = 0;
            reports.forEach((report) => {
                const packages = report?.packageVersions ?? [];
                if (!packages.length) {
                    // No installed-package data for this device, so nothing can be offered for
                    // it — but it still receives whatever gets selected (noted in the UI).
                    missingReports++;
                    return;
                }
                packages
                    .filter((pkg) => pkg.name?.startsWith("os-") && pkg.upgradableTo)
                    .forEach((pkg) => add(pkg.name!, pkg.upgradableTo!));
                INSTALLABLE_CORE_PACKAGES
                    .filter((name) => !packages.some((pkg) => pkg.name === name))
                    .forEach((name) => add(name, FRESH_INSTALL_VERSION));
            });

            setRows(
                Array.from(byName.entries())
                    .map(([name, entry]) => ({
                        id: name,
                        name,
                        availableVersions: Array.from(entry.versions).sort(compareVersions).join(", "),
                        deviceCount: entry.deviceCount,
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name)),
            );
            setNoReportCount(missingReports);
        })();
        return () => {
            cancelled = true;
        };
    }, [devices]);

    const toggle = (name: string) => {
        const next = { ...selected, [name]: !selected[name] };
        setSelected(next);
        onSelectionChange(Object.keys(next).filter((key) => next[key]));
    };

    if (!rows) {
        return (
            <div className="flex items-center gap-2">
                <Spinner />
                <span>Loading package reports for {devices.length} device{devices.length === 1 ? "" : "s"}...</span>
            </div>
        );
    }

    const columns: SortableTableHeaderType<BatchUpgradeRow>[] = [
        {
            header: "",
            renderFunc: (row) => (
                <input
                    type="checkbox"
                    checked={!!selected[row?.name!]}
                    onChange={() => toggle(row?.name!)}
                />
            ),
            width: 30,
        },
        {
            header: "Package",
            dataKey: "name",
            width: 220,
        },
        {
            header: "Available Version",
            dataKey: "availableVersions",
            renderFunc: (row) => <div className="text-green-600">{row?.availableVersions}</div>,
            width: 200,
        },
        {
            header: "Devices",
            dataKey: "deviceCount",
            renderFunc: (row) => `${row?.deviceCount} of ${devices.length}`,
            width: 100,
        },
    ];

    return (
        <>
            <div className="mb-3 text-sm text-muted-body-text">
                Selected packages will be installed or upgraded on all {devices.length} selected device{devices.length === 1 ? "" : "s"}.
            </div>
            {noReportCount > 0 && (
                <div className="mb-3 text-sm text-muted-body-text">
                    {noReportCount} device{noReportCount === 1 ? " has" : "s have"} not reported installed packages, so no upgrades could be listed for {noReportCount === 1 ? "it" : "them"} — selected packages are still sent there.
                </div>
            )}
            <SortableTable
                initialSortDir="asc"
                initialSortKey="name"
                columns={columns}
                data={rows}
                emptyValue={<div className="my-5">No core package upgrades were found for the selected devices.</div>}
            />
        </>
    );
};
